import React, { useState, useEffect } from 'react';
import { apiClient } from '../config/api';
import { Play, RotateCcw, TrendingDown, TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';

const DEFAULT_PARAMS = {
  short_sellers: [],
  sectors: [],
  tickers: [],
  signal_start: '2023-01-01',
  signal_end: new Date().toISOString().slice(0, 10),
  entry_days_after_signal: 1,
  hold_days: 30,
  direction: 'short',
  stop_loss_pct: '',
  take_profit_pct: '',
  position_size_pct: 0.02,
  initial_capital: 1000000,
};

const fmt = (n, d = 2) => n == null ? '—' : Number(n).toFixed(d);
const fmtMoney = (n) => n == null ? '—' : `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

export default function BacktestLive() {
  const [params, setParams]     = useState(DEFAULT_PARAMS);
  const [meta, setMeta]         = useState({ short_sellers: [], sectors: [] });
  const [result, setResult]     = useState(null);
  const [running, setRunning]   = useState(false);
  const [error, setError]       = useState(null);
  const [tab, setTab]           = useState('metrics');

  useEffect(() => {
    Promise.all([
      apiClient.get('/api/backtest-service/meta/short-sellers'),
      apiClient.get('/api/backtest-service/meta/sectors'),
    ]).then(([ss, sec]) => {
      setMeta({
        short_sellers: ss.data.short_sellers || [],
        sectors: (sec.data.sectors || []).filter(Boolean),
      });
    }).catch(() => {});
  }, []);

  const set = (k, v) => setParams(p => ({ ...p, [k]: v }));

  const toggleList = (k, val) => {
    setParams(p => {
      const cur = p[k];
      return { ...p, [k]: cur.includes(val) ? cur.filter(x => x !== val) : [...cur, val] };
    });
  };

  const handleRun = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const body = {
        ...params,
        stop_loss_pct:   params.stop_loss_pct   !== '' ? Number(params.stop_loss_pct)   : null,
        take_profit_pct: params.take_profit_pct !== '' ? Number(params.take_profit_pct) : null,
        position_size_pct: Number(params.position_size_pct),
        initial_capital:   Number(params.initial_capital),
      };
      const res = await apiClient.post('/api/backtest-service/run', body);
      setResult(res.data);
      setTab('metrics');
    } catch (e) {
      const detail = e.response?.data?.detail;
      let errMsg;
      if (Array.isArray(detail))       errMsg = detail.map(d => d.msg || JSON.stringify(d)).join('; ');
      else if (typeof detail === 'string') errMsg = detail;
      else if (detail)                 errMsg = JSON.stringify(detail);
      else                             errMsg = e.message || 'Backtest failed';
      setError(errMsg);
    } finally {
      setRunning(false);
    }
  };

  const m = result?.metrics;
  const winColor  = m?.win_rate >= 50 ? 'text-green-600' : 'text-red-500';
  const retColor  = m?.avg_return_pct >= 0 ? 'text-green-600' : 'text-red-500';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <div>
        <h1 className="text-2xl font-light text-gray-900">Strategy Backtester</h1>
        <p className="text-sm text-gray-500 mt-1">
          Test short-selling strategies against activist report signals · price data via LSEG
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* ── STRATEGY BUILDER ── */}
        <div className="lg:col-span-1 space-y-5">
          <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Signal Filters</h2>

            {/* Short sellers */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">Short Sellers</label>
              <div className="max-h-36 overflow-y-auto space-y-1 pr-1">
                {meta.short_sellers.map(s => (
                  <label key={s} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={params.short_sellers.includes(s)}
                      onChange={() => toggleList('short_sellers', s)}
                      className="rounded border-gray-300 text-blue-600"
                    />
                    <span className="text-xs text-gray-700">{s}</span>
                  </label>
                ))}
              </div>
              {params.short_sellers.length > 0 && (
                <p className="text-xs text-blue-600 mt-1">{params.short_sellers.length} selected</p>
              )}
            </div>

            {/* Sectors */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">Sectors</label>
              <div className="max-h-28 overflow-y-auto space-y-1 pr-1">
                {meta.sectors.map(s => (
                  <label key={s} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={params.sectors.includes(s)}
                      onChange={() => toggleList('sectors', s)}
                      className="rounded border-gray-300 text-blue-600"
                    />
                    <span className="text-xs text-gray-700">{s}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Date range */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Signal From</label>
                <input type="date" value={params.signal_start}
                  onChange={e => set('signal_start', e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Signal To</label>
                <input type="date" value={params.signal_end}
                  onChange={e => set('signal_end', e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Trade Rules</h2>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Direction</label>
              <div className="flex gap-2">
                {['short', 'long'].map(d => (
                  <button key={d} onClick={() => set('direction', d)}
                    className={`flex-1 py-1.5 text-xs rounded border font-medium transition-colors ${
                      params.direction === d
                        ? 'bg-gray-900 text-white border-gray-900'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-gray-500'
                    }`}>
                    {d === 'short' ? '↓ Short' : '↑ Long'}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Entry (days after signal)</label>
                <input type="number" min="0" max="30" value={params.entry_days_after_signal}
                  onChange={e => set('entry_days_after_signal', Number(e.target.value))}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Hold (days)</label>
                <input type="number" min="1" max="365" value={params.hold_days}
                  onChange={e => set('hold_days', Number(e.target.value))}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Stop Loss (%)</label>
                <input type="number" min="0" max="100" step="1" placeholder="none"
                  value={params.stop_loss_pct}
                  onChange={e => set('stop_loss_pct', e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Take Profit (%)</label>
                <input type="number" min="0" max="500" step="1" placeholder="none"
                  value={params.take_profit_pct}
                  onChange={e => set('take_profit_pct', e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Position Size (% of capital)
              </label>
              <input type="number" min="0.1" max="100" step="0.5"
                value={(params.position_size_pct * 100).toFixed(1)}
                onChange={e => set('position_size_pct', Number(e.target.value) / 100)}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded" />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Initial Capital ($)</label>
              <input type="number" min="1000" step="10000"
                value={params.initial_capital}
                onChange={e => set('initial_capital', Number(e.target.value))}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded" />
            </div>

            <button
              onClick={handleRun}
              disabled={running}
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-gray-900 text-white text-sm font-medium rounded hover:bg-gray-800 disabled:opacity-50 transition-colors"
            >
              {running
                ? <><span className="animate-spin">⟳</span> Running…</>
                : <><Play className="w-4 h-4" /> Run Backtest</>}
            </button>

            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                {error}
              </div>
            )}
          </div>
        </div>

        {/* ── RESULTS ── */}
        <div className="lg:col-span-2">
          {!result && !running && (
            <div className="h-full flex items-center justify-center bg-white border border-gray-200 rounded-lg p-12">
              <div className="text-center text-gray-400">
                <TrendingDown className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Configure a strategy and click Run Backtest</p>
              </div>
            </div>
          )}

          {running && (
            <div className="h-full flex items-center justify-center bg-white border border-gray-200 rounded-lg p-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900 mx-auto mb-4" />
                <p className="text-sm text-gray-500">Fetching prices from LSEG and computing trades…</p>
              </div>
            </div>
          )}

          {result && !running && (
            <div className="space-y-4">
              {/* Summary bar */}
              <div className="grid grid-cols-4 gap-3">
                {[
                  { label: 'Signals', value: result.signals_used, color: 'text-gray-900' },
                  { label: 'Trades', value: m.total_trades, color: 'text-gray-900' },
                  { label: 'Win Rate', value: `${fmt(m.win_rate)}%`, color: winColor },
                  { label: 'Avg Return', value: `${fmt(m.avg_return_pct)}%`, color: retColor },
                ].map(c => (
                  <div key={c.label} className="bg-white border border-gray-200 rounded-lg p-3 text-center">
                    <div className={`text-xl font-bold ${c.color}`}>{c.value}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{c.label}</div>
                  </div>
                ))}
              </div>

              {/* Tabs */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="flex border-b border-gray-200">
                  {['metrics', 'trades', 'equity'].map(t => (
                    <button key={t} onClick={() => setTab(t)}
                      className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors capitalize ${
                        tab === t
                          ? 'border-gray-900 text-gray-900'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}>
                      {t === 'equity' ? 'Equity Curve' : t.charAt(0).toUpperCase() + t.slice(1)}
                    </button>
                  ))}
                  <div className="ml-auto px-4 py-3 text-xs text-gray-400 self-center">
                    Ran in {result.run_duration_seconds}s
                  </div>
                </div>

                {/* METRICS TAB */}
                {tab === 'metrics' && (
                  <div className="p-5 grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {[
                      { label: 'Total Trades',       value: m.total_trades },
                      { label: 'Profitable Trades',  value: m.profitable_trades },
                      { label: 'Win Rate',           value: `${fmt(m.win_rate)}%` },
                      { label: 'Avg Return',         value: `${fmt(m.avg_return_pct)}%` },
                      { label: 'Median Return',      value: `${fmt(m.median_return_pct)}%` },
                      { label: 'Total Return',       value: `${fmt(m.total_return_pct)}%` },
                      { label: 'Annualised Return',  value: `${fmt(m.annualised_return_pct)}%` },
                      { label: 'Sharpe Ratio',       value: fmt(m.sharpe_ratio, 3) },
                      { label: 'Max Drawdown',       value: `${fmt(m.max_drawdown_pct)}%` },
                      { label: 'Avg Hold (days)',    value: fmt(m.avg_hold_days, 1) },
                      { label: 'Best Trade',         value: `${fmt(m.best_trade_pct)}%` },
                      { label: 'Worst Trade',        value: `${fmt(m.worst_trade_pct)}%` },
                      { label: 'With Price Data',    value: m.trades_with_data },
                      { label: 'No Price Data',      value: m.trades_no_data },
                    ].map(r => (
                      <div key={r.label} className="bg-gray-50 rounded p-3">
                        <div className="text-xs text-gray-500 mb-1">{r.label}</div>
                        <div className="text-sm font-semibold text-gray-900">{r.value ?? '—'}</div>
                      </div>
                    ))}
                  </div>
                )}

                {/* TRADES TAB */}
                {tab === 'trades' && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          {['Ticker','Short Seller','Signal','Entry','Exit','Entry $','Exit $','P&L %','Hold','Reason'].map(h => (
                            <th key={h} className="px-3 py-2.5 text-left font-medium text-gray-500 uppercase tracking-wide">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {result.trades.map((t, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-3 py-2 font-mono font-medium text-gray-900">{t.ticker}</td>
                            <td className="px-3 py-2 text-gray-600">{t.short_seller}</td>
                            <td className="px-3 py-2 text-gray-500">{t.signal_date}</td>
                            <td className="px-3 py-2 text-gray-500">{t.entry_date || '—'}</td>
                            <td className="px-3 py-2 text-gray-500">{t.exit_date || '—'}</td>
                            <td className="px-3 py-2 font-mono text-gray-600">{t.entry_price ? `$${fmt(t.entry_price)}` : '—'}</td>
                            <td className="px-3 py-2 font-mono text-gray-600">{t.exit_price  ? `$${fmt(t.exit_price)}`  : '—'}</td>
                            <td className={`px-3 py-2 font-mono font-semibold ${t.pnl_pct == null ? 'text-gray-400' : t.pnl_pct >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                              {t.pnl_pct != null ? `${t.pnl_pct >= 0 ? '+' : ''}${fmt(t.pnl_pct)}%` : '—'}
                            </td>
                            <td className="px-3 py-2 text-gray-500">{t.hold_days_actual ?? '—'}</td>
                            <td className="px-3 py-2">
                              <span className={`px-1.5 py-0.5 rounded text-xs ${
                                t.exit_reason === 'stop_loss'   ? 'bg-red-50 text-red-600' :
                                t.exit_reason === 'take_profit' ? 'bg-green-50 text-green-600' :
                                t.exit_reason === 'no_data'     ? 'bg-gray-100 text-gray-500' :
                                'bg-blue-50 text-blue-600'
                              }`}>{t.exit_reason}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* EQUITY CURVE TAB */}
                {tab === 'equity' && (
                  <div className="p-5">
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-xs">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-3 py-2.5 text-left font-medium text-gray-500 uppercase">Date</th>
                            <th className="px-3 py-2.5 text-right font-medium text-gray-500 uppercase">Equity</th>
                            <th className="px-3 py-2.5 text-right font-medium text-gray-500 uppercase">Drawdown</th>
                            <th className="px-3 py-2.5 text-right font-medium text-gray-500 uppercase">vs Start</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {result.equity_curve.map((row, i) => {
                            const change = ((row.equity - params.initial_capital) / params.initial_capital * 100);
                            return (
                              <tr key={i} className="hover:bg-gray-50">
                                <td className="px-3 py-2 text-gray-500">{row.date}</td>
                                <td className="px-3 py-2 text-right font-mono font-medium text-gray-900">{fmtMoney(row.equity)}</td>
                                <td className={`px-3 py-2 text-right font-mono ${row.drawdown_pct > 5 ? 'text-red-500' : 'text-gray-600'}`}>
                                  {row.drawdown_pct > 0 ? `-${fmt(row.drawdown_pct)}%` : '—'}
                                </td>
                                <td className={`px-3 py-2 text-right font-mono font-medium ${change >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                                  {change >= 0 ? '+' : ''}{fmt(change)}%
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
